import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import NewTicketInlineForm from "@/components/new-ticket-inline-form";
import type { Ticket, Gewerk, Plan, Status, TeamGroup, Typ } from "@mangelapp/shared";
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/hooks/use-alert';
import { ArrowLeft } from "lucide-react";
import { IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';

interface EditTicketInlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket?: Ticket | null; // when provided -> edit mode; otherwise can act as create
  planLocation?: { x: number; y: number; planId: string; zoom: number } | null;
  projectId?: string; // required for create mode; for edit mode derives from ticket
  projectName?: string; // Optional: project name to avoid fetching all projects when editing
  prefetchedPlans?: Plan[];
  prefetchedPriorities?: Array<{ id: string; name: string; displayName?: string; color?: string }>;
  prefetchedStatus?: Status[];
  prefetchedGewerke?: Gewerk[];
  prefetchedTypen?: Typ[];
  prefetchedTeamGroups?: TeamGroup[];
  onUpdated?: () => void;
}

export default function EditTicketInlineModal({
  isOpen,
  onClose,
  ticket,
  planLocation,
  projectId,
  projectName,
  prefetchedPlans,
  prefetchedPriorities,
  prefetchedStatus,
  prefetchedGewerke,
  prefetchedTypen,
  prefetchedTeamGroups,
  onUpdated,
}: EditTicketInlineModalProps) {
  const { t } = useTranslation(['components.ticket']);
  const { confirm } = useAlert();
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const currentTicketIdRef = useRef<string | undefined>(ticket?.id);
  const isTrackingEnabledRef = useRef(false);
  
  // Action button state from form
  const [actionState, setActionState] = useState<{
    canExportPDF: boolean;
    isExporting: boolean;
    isPending: boolean;
    canSave: boolean;
    hasChanges: boolean;
    onExportPDF?: () => void;
    onSave?: () => Promise<boolean>;
    onCancel?: () => void;
  }>({
    canExportPDF: false,
    isExporting: false,
    isPending: false,
    canSave: false,
    hasChanges: false,
  });

  // Determine effective project id
  const effectiveProjectId = ticket?.projectId || projectId || '';

  const handleSave = async () => {
    if (actionState.onSave) {
      setIsSaving(true);
      // Call save and get result - validation must pass to close modal
      const shouldClose = await actionState.onSave();
      if (shouldClose) {
        // Close modal immediately (optimistic save) only if validation passed
        setHasChanges(false);
        onUpdated?.();
        onClose();
      }
      // If validation failed or review dialog shown, keep modal open
      setIsSaving(false);
    }
  };

  const handleSuccess = () => {
    // Modal already closed optimistically, just clean up state
    setIsSaving(false);
    setHasChanges(false);
    onUpdated?.();
  };

  const handleCancel = () => {
    setIsSaving(false);
    setHasChanges(false);
    onClose();
  };

  const handleBackClick = async () => {
    if (hasChanges) {
      const result = await confirm({
        title: t('editModal.confirmDiscard.title'),
        message: t('editModal.confirmDiscard.description'),
        variant: 'destructive'
      });
      if (result) {
        handleCancel();
      }
    } else {
      handleCancel();
    }
  };

  const handleFormStateChange = (isDirty: boolean) => {
    // Check if ticket ID has changed (new ticket loaded)
    if (ticket?.id !== currentTicketIdRef.current) {
      // Reset tracking for new ticket
      currentTicketIdRef.current = ticket?.id;
      isTrackingEnabledRef.current = false;
      setHasChanges(false);
      return;
    }

    // If tracking is not enabled yet, wait for clean state signal
    if (!isTrackingEnabledRef.current) {
      if (isDirty === false) {
        // Form reset complete - form is clean, start tracking changes
        isTrackingEnabledRef.current = true;
        setHasChanges(false);
      }
      // If still dirty during initialization, ignore (still initializing)
      return;
    }

    // Tracking is enabled - track all form state changes
    setHasChanges(isDirty);
  };

  // Reset form state when modal opens/closes or ticket changes
  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setHasChanges(false);
      
      // Update current ticket ID reference if ticket changed
      if (ticket?.id !== currentTicketIdRef.current) {
        currentTicketIdRef.current = ticket?.id;
        isTrackingEnabledRef.current = false;
      }
    } else {
      // Clean up when modal closes
      setHasChanges(false);
      isTrackingEnabledRef.current = false;
    }
  }, [isOpen, ticket?.id]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={async (open) => {
        if (!open) {
          if (hasChanges) {
            const result = await confirm({
              title: t('editModal.confirmDiscard.title'),
              message: t('editModal.confirmDiscard.description'),
              variant: 'destructive'
            });
            if (result) {
              handleCancel();
            }
          } else {
            handleCancel();
          }
        }
      }}>
        <DialogContent className="ticket-modal-shell [&>button]:hidden">
          <div className="ticket-modal-header">
            <div className="ticket-modal-title-row">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackClick}
                className="h-8 w-8 p-0 text-[#333333] hover:text-[#333333] hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <DialogTitle className="ticket-modal-title">
                {ticket
                  ? t('editModal.editTitle')
                  : t('editModal.createTitle')}
              </DialogTitle>
            </div>
            {/* Action buttons moved to header */}
            <div className="ticket-modal-actions">
              <IconButtonCancel
                onClick={() => actionState.onCancel?.()} 
                size={30}
                title={t('editModal.actions.cancel') || 'Cancel'}
              />
              <IconButtonSave
                onClick={handleSave}
                disabled={!actionState.canSave}
                size={30}
                title={actionState.isPending ? (t('editModal.actions.saving') || 'Saving...') : (ticket ? (t('editModal.actions.save') || 'Save') : (t('editModal.actions.create') || 'Create'))}
              />
            </div>
          </div>

          <DialogDescription className="sr-only">
            {ticket
              ? (t('editModal.descriptionEdit') as string)
              : (t('editModal.description') as string)}
          </DialogDescription>

          <div className="ticket-modal-scroll">
            <NewTicketInlineForm
              projectId={effectiveProjectId}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
              onValidationError={() => setIsSaving(false)}
              editTicket={ticket || undefined}
              planLocation={planLocation || (ticket as any)?.planLocation}
              hidePlanPreview={false}
              projectName={projectName}
              prefetchedPlans={prefetchedPlans}
              prefetchedPriorities={prefetchedPriorities}
              prefetchedStatus={prefetchedStatus}
              prefetchedGewerke={prefetchedGewerke}
              prefetchedTypen={prefetchedTypen}
              prefetchedTeamGroups={prefetchedTeamGroups}
              onFormStateChange={handleFormStateChange}
              onActionsReady={(actions) => {
                setActionState(actions);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}
